import type {
  EventProductsResponse,
  FilterProductType,
  Paginated,
  ProductsResponse,
  QueryArgs,
  Sortable,
} from "@ac-expo/api-types";
import { useQuery } from "@tanstack/react-query";
import { privateAxios } from "../axiosInstances";
import productsQueryKeys from "../query-keys/product.query-keys";
import { MAX_PAGE_SIZE_FOR_ALL_ITEMS } from "../constants";

const getStructureProducts = async (
  structureId: string,
  params: Sortable<Paginated<FilterProductType>>
) => {
  const res = await privateAxios.get<ProductsResponse>(
    `/structures/${structureId}/products`,
    { params }
  );
  return res.data;
};

export const useGetStructureProductsQuery = (
  structureId: string,
  params: Sortable<Paginated<FilterProductType>>,
  options?: QueryArgs<ProductsResponse>
) => {
  return useQuery({
    queryKey: productsQueryKeys.filteredList(structureId, params),
    queryFn: () => getStructureProducts(structureId, params),
    enabled: structureId !== "",
    ...options,
  });
};

export const useGetAllStructureProductsQuery = (
  structureId?: string,
  options?: QueryArgs<ProductsResponse>
) => {
  const defaultParams: Sortable<Paginated<FilterProductType>> = {
    searchText: "",
    pagination: { pageIndex: 0, pageSize: MAX_PAGE_SIZE_FOR_ALL_ITEMS },
    sorting: [],
  };

  return useQuery({
    queryKey: productsQueryKeys.filteredList(
      structureId ?? "mine",
      defaultParams
    ),
    queryFn: () => getStructureProducts(structureId ?? "mine", defaultParams),
    enabled: structureId !== "",
    ...options,
  });
};

const getEventProducts = async (structureId: string, eventId: string) => {
  const res = await privateAxios.get<EventProductsResponse>(
    `/structures/${structureId}/products/events/${eventId}`
  );
  return res.data;
};

export const useGetEventProductsQuery = (
  structureId: string,
  eventId: string
) => {
  return useQuery({
    queryKey: productsQueryKeys.eventList(structureId, eventId),
    queryFn: () => getEventProducts(structureId, eventId),
    enabled: !!structureId && !!eventId,
  });
};
